import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IImageSliderProps } from "./ImageSlider.interface";
import { TranslationContext } from "../../../shared/providers/translation/translation.provider";
import { ModuleContext } from "../../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL } from "../../../shared/constants";
import { ComponentTypes, ItemTypes } from "../../../shared/interfaces/modules.interface";
import { useDrag } from "../../../shared/hooks/useDrag";
import Button from "../../Button/Button";

function ImageSlider(props: IImageSliderProps) {
    const { lang } = useContext(TranslationContext);
    const slider = useRef<HTMLDivElement>(null);
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const { progress, setProgress } = useContext(ModuleContext);

    const handleDrag = (delta: number) => {
        if (!stage.current) return;
        (stage.current as HTMLElement).scrollBy(delta, 0);
    };

    const { ref: stage } = useDrag({
        onDragLeft: handleDrag,
        onDragRight: handleDrag
    });

    const intersectionObserver = useMemo(() => {
        return new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const element = entry.target as HTMLImageElement;
                    setCurrentSlide(Number(element.dataset.index));

                    if (
                        element.parentElement?.lastChild === element &&
                        progress?.progress !== 100 &&
                        lang
                    ) {
                        setProgress({
                            unitId: props.unitId,
                            itemId: props.id,
                            progress: 100,
                            itemType: ComponentTypes.IMAGE,
                            itemLocale: lang
                        });
                    }
                });
            },
            { root: stage.current, threshold: 1 }
        );
    }, [setCurrentSlide, setProgress, progress, stage]);
    
    useEffect(() => {
        if (!slider || !intersectionObserver) return;
        const imgs = slider.current?.querySelectorAll(".slider_image-stage>img");
        imgs?.forEach((img) => intersectionObserver.observe(img));
    }, [slider, intersectionObserver]);

    return (
        <section className="unit_item container-fluid gx-0">
            <div className="row gx-0">
                <div className="col-12">
                    <h2 className="text-h4-bd mb-1">{props.title}</h2>
                    <div className="slider_image is_full" ref={slider}>
                        <div className="slider_image-stage" ref={stage}>
                            {props.images.map((img, i) => (
                                <img
                                    key={i}
                                    src={`${CONTENT_BASE_URL}${img.url}`}
                                    alt={img.alt}
                                    data-index={i}></img>
                            ))}
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="slider_image-navigation">
                                {props.images.map((_, i) => (
                                    <span
                                        key={i}
                                        className={`navigation_item ${i === currentSlide ? "is_active" : ""}`}></span>
                                ))}
                            </div>
                            <div className="slider_image-navigation ms-auto">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        const prevSlide =
                                            slider.current?.querySelector(".slider_image-stage")
                                                ?.children[currentSlide - 1];

                                        if (prevSlide)
                                            setCurrentSlide(
                                                Number((prevSlide as HTMLElement).dataset.index)
                                            );

                                        prevSlide?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                            inline: "center",
                                            container: "nearest"
                                        } as ScrollIntoViewOptions);
                                    }}
                                    variant="solid"
                                    customCss="p-02 color-neutrals-white"
                                    icon="Chevron--left"
                                    iconSize={16}
                                />
                                <Button
                                    type="button"
                                    onClick={() => {
                                        const nextSlide =
                                            slider.current?.querySelector(".slider_image-stage")
                                                ?.children[currentSlide + 1];

                                        if (nextSlide)
                                            setCurrentSlide(
                                                Number((nextSlide as HTMLElement).dataset.index)
                                            );

                                        nextSlide?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                            inline: "center",
                                            container: "nearest"
                                        } as ScrollIntoViewOptions);
                                    }}
                                    variant="solid"
                                    customCss="p-02 color-neutrals-white"
                                    icon="Chevron--right"
                                    iconSize={16}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ImageSlider;

function SliderImageContent() {
    return;
}
